import React, { ReactNode } from 'react';
import cx from 'classnames';
import _has from 'lodash/has';
import _get from 'lodash/get';

import { LoadingEllipses } from '..';

import { colorStates } from './constants';

import './button.css';

type BaseButtonProps = {
    /**
     * Any CSS classes to add to the <button>
     */
    addClasses?: string;

    /**
     * If true, a box shadow will be added to the button
     * @defaultValue false
     */
    boxShadow?: boolean;
    children?: ReactNode;
    /**
     * A preset color style
     */
    color: ButtonColor;
    /**
     * A unique ID for testing with Cypress; will be added to the <button> as its `data-cy` attribute
     */
    dataCy?: string;
    disabled?: boolean;
    /**
     * Click handler for the <button>
     */
    handleClick?(): void;
    /**
     * `id` attribute for the <button>
     */
    id?: string;

    /**
     * Padding to apply inside button
     * @defaultValue 'lg'
     */
    padding?: 'xs' | 'sm' | 'lg' | 'none';
    /**
     * Tab index to apply to the <button>
     */
    tabIndex?: number;
};

type AutoWidthButtonProps = {
    /**
     * Whether to add the `w-auto` class to the <button>
     */
    autoWidth?: boolean;
};

type LoadingButtonProps = {
    /**
     * When true, an animated ellipses will replace the children
     */
    loading: boolean;
    /**
     * Sets the width of the button so that it will not shift when the button is in a loading state
     */
    width: number;
};

type ButtonProps = BaseButtonProps &
    (AutoWidthButtonProps | LoadingButtonProps);

export const Button = (props: ButtonProps): JSX.Element => {
    const {
        addClasses,
        boxShadow = false,
        children,
        color,
        dataCy,
        disabled = false,
        handleClick,
        id,
        padding = 'lg',
        tabIndex,
    } = props;

    let autoWidth = false;
    let width = null;
    let loading = false;

    if ('autoWidth' in props) {
        autoWidth = props?.autoWidth ?? false;
    }

    if ('width' in props) {
        width = props?.width;
        ({ loading } = props);
    }

    let classNames = cx(
        'button',
        'relative',
        'rounded',
        'text-left',
        addClasses,
        color,
        {
            'p-1': padding === 'xs',
            'p-2': padding === 'sm',
            'p-3': padding === 'lg',
            'w-full': width === null && !autoWidth,
            'w-auto': autoWidth,
            'cursor-default': disabled,
            boxShadow,
        },
    );

    if (_has(colorStates, color)) {
        const addColorStates = _get(colorStates, color, []);

        classNames = cx(classNames, addColorStates.join(' '));
    }

    return (
        <button
            disabled={disabled}
            id={id}
            type="button"
            className={classNames}
            onClick={(): void => handleClick && handleClick()}
            tabIndex={tabIndex}
            data-cy={dataCy}
            style={{ width: width ?? undefined }}
        >
            {!loading && <>{children}</>}
            {loading && (
                <div className="text-center">
                    <LoadingEllipses width={40} />
                </div>
            )}
        </button>
    );
};
